<template>
  <div class="w-full h-screen relative bg-[#b7cef2] overflow-hidden">
    <!-- Imagen en la parte superior -->
    <img
      class="w-[350px] h-[210px] absolute top-0 left-[50%] transform -translate-x-[50%] z-10"
      src="https://upload.wikimedia.org/wikipedia/commons/2/2b/Ziply_Fiber_logo.svg"
    />
    <!-- Contenedor de la tarjeta con scroll vertical y margen superior -->
    <div class="absolute top-[210px] left-0 right-0 h-[calc(100%-210px)] mt-6">
      <div class="w-[500px] mx-auto bg-white rounded-xl shadow-lg p-10 flex flex-col gap-5">
        <div class="self-stretch justify-center items-center gap-[105px] inline-flex">
          <div class="text-neutral-900 text-xl font-semibold font-['Public Sans'] leading-7">
            Register User
          </div>
        </div>
        <div class="w-full flex flex-col gap-5">
          <!-- First Name -->
          <div class="w-full flex-col justify-start items-start gap-1.5">
            <div class="w-full text-slate-600 text-base text-left font-medium font-['Public Sans'] leading-relaxed">
              First Name
            </div>
            <input
              v-model="firstName"
              type="text"
              placeholder="First Name"
              :class="{
                'w-full px-3 py-2 capitalize bg-white rounded-lg shadow border-2 border-red-400 border-solid':
                  !firstName,
                'w-full px-3 py-2 capitalize bg-white rounded-lg shadow border-2 border-green-400 border-solid':
                  firstName,
              }"
            />
          </div>

          <!-- Last Name -->
          <div class="w-full flex-col justify-start items-start gap-1.5">
            <div class="w-full text-slate-600 text-base text-left font-medium font-['Public Sans'] leading-relaxed">
              Last Name
            </div>
            <input
              v-model="lastName"
              type="text"
              placeholder="Last Name"
              :class="{
                'w-full px-3 py-2 capitalize bg-white rounded-lg shadow border-2 border-red-400 border-solid':
                  !lastName,
                'w-full px-3 py-2 capitalize bg-white rounded-lg shadow border-2 border-green-400 border-solid':
                  lastName,
              }"
            />
          </div>

          <!-- Email -->
          <div class="w-full flex-col justify-start items-start gap-1.5">
            <div class="w-full text-slate-600 text-base text-left font-medium font-['Public Sans'] leading-relaxed">
              Email
            </div>
            <input
              v-model="email"
              type="email"
              placeholder="mail@example.com"
              :class="{
                'w-full px-3 py-2 bg-white rounded-lg shadow border-2 border-red-400 border-solid':
                  !isEmailValid(email),
                'w-full px-3 py-2 bg-white rounded-lg shadow border-2 border-green-400 border-solid':
                  isEmailValid(email),
              }"
            />
            <div class="flex items-center text-xs mt-2">
              <div class="text-red-500 font-bold mr-1">
                Important:
                <span class="text-blue_primary"
                  >Please register using the same email address you used for the
                  Ziply Rewards Program.</span
                >
              </div>
            </div>
          </div>

          <!-- Password -->
          <div class="w-full flex-col justify-start items-start gap-1.5 relative">
            <div class="w-full text-slate-600 text-base text-left font-medium font-['Public Sans'] leading-relaxed">
              Password
            </div>
            <div class="relative w-full">
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="********"
                :class="{
                  'w-full px-3 py-2 bg-white rounded-lg shadow border-2 border-red-400 border-solid':
                    !isPasswordValid(password),
                  'w-full px-3 py-2 bg-white rounded-lg shadow border-2 border-green-400 border-solid':
                    isPasswordValid(password),
                }"
              />
              <button
                type="button"
                @click="toggleShowPassword"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue_primary"
              >
                {{ showPassword ? "Hide" : "Show" }}
              </button>
            </div>

            <div class="text-xs text-red-500 mt-2">
              <ul>
                <li :class="{ 'text-green-500': isPasswordLongEnough }">
                  Minimum 6 characters
                </li>
                <li :class="{ 'text-green-500': hasUpperCaseLetter }">
                  At least one uppercase letter
                </li>
                <li :class="{ 'text-green-500': hasSpecialCharacter }">
                  At least one special character
                </li>
              </ul>
            </div>
          </div>

          <!-- Confirm Password -->
          <div class="w-full flex-col justify-start items-start gap-1.5 relative">
            <div class="w-full text-slate-600 text-base text-left font-medium font-['Public Sans'] leading-relaxed">
              Confirm Password
            </div>
            <div class="relative w-full">
              <input
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="********"
                :class="{
                  'w-full px-3 py-2 bg-white rounded-lg shadow border-2 border-red-400 border-solid':
                    !confirmPassword || confirmPassword !== password,
                  'w-full px-3 py-2 bg-white rounded-lg shadow border-2 border-green-400 border-solid':
                    confirmPassword === password,
                }"
              />
              <button
                type="button"
                @click="toggleShowConfirmPassword"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue_primary"
              >
                {{ showConfirmPassword ? "Hide" : "Show" }}
              </button>
            </div>
          </div>

          <div class="w-full justify-end items-center gap-3 inline-flex">
            <button
              @click="handleCancel"
              class="px-3.5 py-[9px] bg-white rounded-lg shadow border border-slate-200 flex justify-center items-center gap-2"
            >
              <div class="text-slate-600 text-sm font-semibold font-['Public Sans'] leading-snug">
                Cancel
              </div>
            </button>
            <button
              @click="handleRegister"
              :disabled="!isFormValid"
              :class="{
                'px-3.5 py-[9px] bg-blue-300 rounded-lg shadow flex justify-center items-center gap-2':
                  !isFormValid,
                'px-3.5 py-[9px] bg-blue_primary rounded-lg shadow flex justify-center items-center gap-2':
                  isFormValid,
              }"
            >
              <div class="text-white text-sm font-semibold font-['Public Sans'] leading-snug">
                Register
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>



<script>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

export default {
  components: {},
  setup() {
    const store = useStore();
    const router = useRouter();

    const flagLoading = ref(false);
    const firstName = ref("");
    const lastName = ref("");
    const email = ref("");
    const password = ref("");
    const confirmPassword = ref("");
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);

    const isEmailValid = (email) => /\S+@\S+\.\S+/.test(email);

    const isPasswordLongEnough = computed(() => password.value.length >= 6);
    const hasUpperCaseLetter = computed(() => /[A-Z]/.test(password.value));
    const hasSpecialCharacter = computed(() =>
      /[!@#$%^&*(),.?":{}|<>]/.test(password.value)
    );

    const isPasswordValid = (password) => {
      const minLength = 6;
      const hasUpperCase = /[A-Z]/.test(password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
      return password.length >= minLength && hasUpperCase && hasSpecialChar;
    };

    const isFormValid = computed(() => {
      return (
        firstName.value &&
        lastName.value &&
        isEmailValid(email.value) &&
        isPasswordValid(password.value) &&
        confirmPassword.value === password.value
      );
    });

    const toggleShowPassword = () => {
      showPassword.value = !showPassword.value;
    };

    const toggleShowConfirmPassword = () => {
      showConfirmPassword.value = !showConfirmPassword.value;
    };

    const handleRegister = () => {
      flagLoading.value = true;
      if (isFormValid.value) {
        store
          .dispatch("addUser", {
            name: firstName.value,
            last_name: lastName.value,
            email: email.value,
            password: password.value,
          })
          .then((newAccO) => {
            console.log("🚀 ~ .then ~ newAccO:", newAccO);
            firstName.value = "";
            lastName.value = "";
            email.value = "";
            password.value = "";
            confirmPassword.value = "";

            if (newAccO.ok) {
              flagLoading.value = false;
              router.push({ name: "Login" });
            }
          })
          .catch((error) => {
            console.error("Registration error:", error);
            flagLoading.value = false;
          });
      }
    };

    const handleCancel = () => {
      firstName.value = "";
      lastName.value = "";
      email.value = "";
      password.value = "";
      confirmPassword.value = "";

      router.push({ name: "Login" });
    };

    return {
      isPasswordLongEnough,
      hasUpperCaseLetter,
      hasSpecialCharacter,
      flagLoading,
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      isEmailValid,
      isPasswordValid,
      isFormValid,
      toggleShowPassword,
      showPassword,
      toggleShowConfirmPassword,
      showConfirmPassword,
      handleRegister,
      handleCancel,
    };
  },
};
</script>

<style scoped></style>
